import { useState, type ChangeEvent } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { useMutation } from "util/graphql";
import Button from "common/core/button";
import MaskedInput from "common/form/inputs/masked_input";
import { MutationErrorModal } from "common/settingsv2/modals/mutation_error_modal";
import { NOTIFICATION_TYPES } from "constants/notifications";
import { pushNotification } from "common/core/notification_center/actions";

import GENERATE_TOTP_SECRET_MUTATION from "./generate_totp_secret_mutation.graphql";
import SUBMIT_TOTP_SETUP_MUTATION from "./submit_totp_setup_mutation.graphql";
import DELETE_TOTP_MUTATION from "./delete_totp_mutation.graphql";
import Styles from "./index.module.scss";

type TotpSetupState =
  | {
      kind: "generate";
      status: null | string;
      errorMsg: null | string;
    }
  | {
      kind: "confirm";
      qrSvg: string;
      code: string;
      status: null | "loading" | "success" | "error";
      errorMsg: null | string;
    };

const MESSAGES = defineMessages({
  success: {
    id: "3db2214c-d2e8-459a-b3d3-6da4420a74a1",
    defaultMessage: "You have successfully set up your authenticator app.",
  },
  invalidCodeError: {
    id: "0392f385-19ee-4e17-a3c3-5871d46c24f7",
    defaultMessage: "The code you entered was invalid. Please try again.",
  },
  mutationError: {
    id: "d6bd8a3a-72f5-4c7f-a877-96a8654a53fb",
    defaultMessage:
      "We are unable to set up your authenticator app at this time. Please try again or reach out to customer support.",
  },
  label: {
    id: "f7171c20-934e-4773-9a65-5693cf1bb393",
    defaultMessage: "QR Code",
  },
});

function TotpSetup() {
  const intl = useIntl();
  const [state, setState] = useState<TotpSetupState>({
    kind: "generate",
    status: null,
    errorMsg: null,
  });
  const generateTotpSecretMutateFn = useMutation(GENERATE_TOTP_SECRET_MUTATION);
  const submitTotpSetupMutateFn = useMutation(SUBMIT_TOTP_SETUP_MUTATION);

  const generateSecret = async () => {
    setState({ kind: "generate", status: "loading", errorMsg: null });
    const { data } = await generateTotpSecretMutateFn();
    setState({
      kind: "confirm",
      qrSvg: btoa(data!.generateTotpSecret!.qrStream),
      code: "",
      status: "success",
      errorMsg: null,
    });
  };
  const submitTotp = async () => {
    if (state.kind === "confirm") {
      setState((state) => ({ ...state, status: "loading" }));
      try {
        await submitTotpSetupMutateFn({ variables: { input: { secret: state.code } } });
        setState((state) => ({ ...state, status: "success" }));
        pushNotification({
          type: NOTIFICATION_TYPES.DEFAULT,
          message: intl.formatMessage(MESSAGES.success),
        });
      } catch (e) {
        if ((e as Error).message.includes("invalid_code")) {
          setState((state) => ({
            ...state,
            status: "error",
            errorMsg: intl.formatMessage(MESSAGES.invalidCodeError),
          }));
        } else {
          setState((state) => ({
            ...state,
            status: "error",
            errorMsg: intl.formatMessage(MESSAGES.mutationError),
          }));
        }
      }
    }
  };

  if (state.kind === "generate") {
    return (
      <Button
        onClick={generateSecret}
        isLoading={state.status === "loading"}
        buttonColor="action"
        variant="primary"
      >
        <FormattedMessage id="0c8340e3-0d2c-4b7f-babc-b74d03e495e8" defaultMessage="Set Up" />
      </Button>
    );
  }

  return (
    <>
      <p id="totp-info" className={Styles.info}>
        <FormattedMessage
          id="4953927d-9718-4c5b-bf97-6f95caa32af2"
          defaultMessage="Install an authenticator app (like Google Authenticator). Scan the QR code from within the app. Enter the code generated by the app below to confirm the device."
        />
      </p>
      <img
        className={Styles.qrCode}
        src={`data:image/svg+xml;base64,${state.qrSvg}`}
        alt="qr code"
      />
      <MaskedInput
        aria-describedby="totp-info"
        className={Styles.totpCode}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const code = e.target.value;
          setState((state) => ({ ...state, code }));
        }}
        value={state.code}
        label={
          <FormattedMessage id="7b5c8684-0f89-45d0-bbcc-92e4d7ca6951" defaultMessage="Enter code" />
        }
        maskType="number"
        maxlength={6}
        aria-label={intl.formatMessage(MESSAGES.label)}
        placeholder="000000"
        autoFocus
        useStyledInput
      />
      <Button
        onClick={submitTotp}
        isLoading={state.status === "loading"}
        disabled={state.code.length !== 6}
        buttonColor="action"
        variant="primary"
      >
        <FormattedMessage
          id="1d863677-46d3-488f-bd7d-4f0876153d04"
          defaultMessage="Complete Setup"
        />
      </Button>
      {state.status === "error" && (
        <MutationErrorModal
          message={state.errorMsg}
          onClick={() => {
            setState((state) => ({
              ...state,
              status: null,
              errorMsg: null,
            }));
          }}
        />
      )}
    </>
  );
}

function TotpDisplay() {
  const deleteTotpMutateFn = useMutation(DELETE_TOTP_MUTATION);
  const [loading, setLoading] = useState(false);

  const clearTotp = () => {
    setLoading(true);
    deleteTotpMutateFn();
  };

  return (
    <Button onClick={clearTotp} isLoading={loading} buttonColor="action" variant="primary">
      <FormattedMessage
        id="e9589908-bb32-4aab-85dd-dde096b55472"
        defaultMessage="Clear Authenticator App Settings"
      />
    </Button>
  );
}

function TotpSettings({ isSetup }: { isSetup: boolean }) {
  return isSetup ? <TotpDisplay /> : <TotpSetup />;
}

export { TotpSettings };
